import { useState } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";

const ENUM_LABELS = {
  natureOfWork: { 1: "Field Job", 2: "Work from Office", 3: "Work from Home", 4: "Hybrid" },
  workShift: { 1: "Day Shift", 2: "Night Shift", 3: "Rotational Shift", 4: "Flexible Shift" },
  candidateJoiningTimeFrame: { 1: "Immediately", 2: "Within 15 days", 3: "Within 1 month" },
  salaryStructure: { 1: "Fixed Salary", 2: "Incentive Salary", 3: "Fixed and Incentive both" },
  gender: { 1: "Female", 2: "Male", 3: "All" },
  englishRequirement: { 1: "Not required", 2: "Basic English", 3: "Good English", 4: "Fluent English" },
  minimumEducation: { 1: "Any", 2: "10th pass", 3: "12th pass", 4: "Diploma after 10th", 5: "Diploma after 12th", 6: "Graduate", 7: "Post Graduate", 8: "ITI" },
  minimumAcademicScore: { 1: "Any", 2: "First Division", 3: "Second Division", 4: "Third Division" },
  acceptedCandidateLocations: { 1: "Any", 2: "Same state as job location only", 3: "Migrant only", 4: "Selected states only" },
  candidateSocialCategory: { 1: "Open to all", 2: "General", 3: "SC", 4: "ST", 5: "OBC" },
};

const formatLabel = (value) => value
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/[_-]+/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

export const formatFormValue = (value, key = "") => {
  if (value === null || value === undefined || value === "") return "--";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (ENUM_LABELS[key]?.[value]) return ENUM_LABELS[key][value];
  if (Array.isArray(value)) {
    if (!value.length) return "--";
    if (value.every((item) => typeof item !== "object" || item === null)) {
      return value.map((item) => formatLabel(String(item))).join(", ");
    }
  }
  return String(value);
};

const hasEmployerValue = (value) => {
  if (value === null || value === undefined || value === "" || value === 0) return false;
  if (Array.isArray(value)) return value.some(hasEmployerValue);
  if (typeof value === "object") return Object.values(value).some(hasEmployerValue);
  return true;
};

export const flattenFormFields = (value, prefix = "") => Object.entries(value || {}).flatMap(([key, fieldValue]) => {
  if (!hasEmployerValue(fieldValue) && typeof fieldValue !== "boolean") return [];
  const label = prefix ? `${prefix} - ${formatLabel(key)}` : formatLabel(key);
  if (Array.isArray(fieldValue) && fieldValue.some((item) => item && typeof item === "object")) {
    return fieldValue.flatMap((item, index) => flattenFormFields(item, `${label} ${index + 1}`));
  }
  if (fieldValue && typeof fieldValue === "object" && !Array.isArray(fieldValue)) {
    return flattenFormFields(fieldValue, label);
  }
  return [{ label, value: formatFormValue(fieldValue, key) }];
});

const hasPositiveValue = (value) => Object.values(value || {}).some((item) => Number(item) > 0);

const positiveValues = (value) => Object.fromEntries(Object.entries(value || {}).filter(([, item]) => Number(item) > 0));

const formatExperienceMonths = (months) => {
  const totalMonths = Number(months);
  if (!Number.isFinite(totalMonths) || totalMonths < 0) return "--";
  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;
  return [years ? `${years}y` : "", remainingMonths ? `${remainingMonths}m` : ""].filter(Boolean).join(" - ") || "0m";
};


export const getAdditionalPromptTexts = (job = {}) => [...new Set([
  ...(job.jobDescription?.additionalPrompt || []),
  ...(job.additionalPrompt || []),
].map((prompt) => prompt?.originalText?.toString().trim()).filter(Boolean))];

export const getFormSections = (job = {}) => {
  const description = job.jobDescription || {};
  const location = description.jobLocation || {};
  const salary = job.salary || {};
  return [
    {
      title: "A. Category",
      value: { "Job role": job.jobRoleNameEn },
    },
    {
      title: "B. Description",
      value: {
        "Are you posting for outside India?": job.isInternationalJob,
        "Do you want to post jobs for multiple locations?": Number(job.numberOfLocations) > 1,
        "Number of locations": job.numberOfLocations,
        "Job location": {
          Address: location.address,
          Pincode: location.pincode,
          City: location.city,
          State: location.state,
          Country: location.country,
        },
        "Number of vacancies": description.numberOfVacancies,
        "Nature of work": formatFormValue(description.natureOfWork, "natureOfWork"),
        "Work shift": formatFormValue(description.workShift, "workShift"),
        "Is it a part-time job?": description.isPartTime,
        ...(description.isPartTime ? { "Daily time commitment (hours)": description.dailyCommitmentHours } : {}),
        "Is it a contract job?": description.isContractJob,
        ...(description.isContractJob ? { "Tenure of contract (months)": description.contractDuration } : {}),
        "Is this job part of a continuous hiring drive?": description.isContinuosHiringDrive,
        ...(description.isContinuosHiringDrive ? { "Hiring drive duration (months)": description.hiringDriveDurationInMonths } : {}),
        "If selected, how soon can the candidate join?": formatFormValue(description.candidateJoiningTimeFrame, "candidateJoiningTimeFrame"),
      },
    },
    {
      title: "C. Salary",
      value: {
        "Salary structure": salary.salaryStructure ? formatFormValue(salary.salaryStructure, "salaryStructure") : undefined,
        ...(hasPositiveValue(salary.salary) ? { "Monthly Net in-hand salary": positiveValues(salary.salary) } : {}),
        ...(hasPositiveValue(salary.incentive) ? { "Monthly Incentive": positiveValues(salary.incentive) } : {}),
        ...(hasPositiveValue(salary.monthlyDeduction) ? { "Monthly deductions": positiveValues(salary.monthlyDeduction) } : {}),
      },
    },
    {
      title: "D. Benefits",
      value: { "Applicable job benefits": job.jobBenefits },
    },
    {
      title: "E. Interview",
      value: { "Interview rounds": job.interview },
    },
    {
      title: "F. General Requirements",
      value: {
        "Candidate age preference": job.age,
        "Candidate gender": formatFormValue(job.gender, "gender"),
        "Does the workplace welcome other genders?": job.otherGendersAllowed,
        "Mandatory assets required for the job": job.assetsForJob,
      },
    },
    {
      title: "G. Work Experience",
      value: {
        "Relevant work experience - Min Work Exp": formatExperienceMonths(job.experience?.minWorkExp ?? job.experience?.min_work_exp),
        "Relevant work experience - Max Work Exp": formatExperienceMonths(job.experience?.maxWorkExp ?? job.experience?.max_work_exp),
      },
    },
    { title: "H. Skill Requirement", value: { "English language requirement": job.skillRequirement?.englishRequirement ? formatFormValue(job.skillRequirement.englishRequirement, "englishRequirement") : undefined } },
    { title: "I. Qualification", value: { Qualification: job.qualification } },
    { title: "J. Physical Fitness", value: { "Physical fitness requirements": job.physicalFitness } },
    { title: "K. Cultural Fitness", value: { "Cultural fitness requirements": job.culturalFitness } },
    {
      title: "L. Background Check",
      value: { "Background check requirements": job.backgroundCheck },
    },
  ];
};

const JobFormDetailsButton = ({ job = {} }) => {
  const [open, setOpen] = useState(false);
  const additionalPrompts = getAdditionalPromptTexts(job);

  return (
    <>
      <Button size="small" variant="outlined" disabled={!job?._id} onClick={() => setOpen(true)}>
        View Form
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Job Form Details — {job.jobId || job.jobRoleNameEn || "Job"}
          <IconButton aria-label="Close form details" onClick={() => setOpen(false)} sx={{ position: "absolute", right: 12, top: 12 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {getFormSections(job).map((section, index) => (
            <Accordion key={section.title} defaultExpanded={index === 0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>{section.title}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {flattenFormFields(section.value).map((field) => (
                    <Grid item xs={12} sm={6} key={field.label}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">{field.label}</Typography>
                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", fontWeight: 600 }}>{field.value}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
          {additionalPrompts.length > 0 && (
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Additional Prompt</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {additionalPrompts.map((prompt, index) => (
                  <Box key={prompt} sx={{ mb: index < additionalPrompts.length - 1 ? 2 : 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      {additionalPrompts.length > 1 ? `Employer prompt ${index + 1}` : "Employer prompt"}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{prompt}</Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

JobFormDetailsButton.propTypes = {
  job: PropTypes.object,
};

export default JobFormDetailsButton;
